import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { type ServiceCost } from '@/content/services';

import { costChipSx } from './serviceCostTokens';

type Props = {
  cost: ServiceCost;
  /** Draw the qualifying clause under the chip. The cards have no room for it. */
  showNote?: boolean;
};

/**
 * What a service costs, as a signal.
 *
 * The one piece of information a catalogue of services reliably withholds and
 * a visitor reliably wants first: which of these are free, which come with
 * the import, and which are a separate bill. It is a signal and never an
 * amount — see `ServiceCost` in the catalogue for why no figure appears here.
 *
 * Colour is not the only carrier. The label says "Free" or "Quoted per job"
 * in words, so the distinction survives greyscale, a colour-blind reader and
 * a printed page; the tint is reinforcement. That is also why the chip is not
 * a bare coloured dot with a tooltip, which was the tidier drawing and the
 * worse component.
 */
export function ServiceCostChip({ cost, showNote = false }: Props) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <Typography component="span" variant="caption" sx={costChipSx(cost.tone)}>
        {cost.label}
      </Typography>
      {showNote && cost.note && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {cost.note}
        </Typography>
      )}
    </Box>
  );
}
